/**
 * Reads of scoring models for every screen that shows them (Settings → Models, the rule editors, the
 * Topic editor, the reader, the banners): the catalogue (`models.catalog()`), the user's model records in
 * order (`models.list()`) and this device's downloads (`models.installs()`).
 *
 * Keys live under ['models', …] (the same as engine-client/keys.ts `modelCatalog` / `modelInstalls`):
 * engine-client/invalidate.ts refreshes them on `model` events and `models` table changes, and
 * app/model-events.ts writes each `model` event's install state straight into ['models', 'installs'].
 */

import {
  toDigesterError,
  type DigesterError,
  type EmbeddingCatalogEntry,
  type Model,
  type ModelCatalog,
  type ModelCatalogEntry,
  type ModelInstallState,
} from '@digester/core';
import { useEngineQuery } from '../../engine-client/EngineProvider.js';

export const modelKeys = {
  all: () => ['models'] as const,
  catalog: () => ['models', 'catalog'] as const,
  list: () => ['models', 'list'] as const,
  installs: () => ['models', 'installs'] as const,
};

export function useCatalog(): ModelCatalog | undefined {
  return useEngineQuery(modelKeys.catalog(), (e) => e.models.catalog()).data;
}

/** The user's model records in model order (one per catalogue entry). */
export function useModels(): { models: Model[]; isLoading: boolean; error: DigesterError | null } {
  const q = useEngineQuery(modelKeys.list(), (e) => e.models.list());
  return { models: q.data ?? [], isLoading: q.isLoading, error: q.error ? toDigesterError(q.error) : null };
}

export interface Installs {
  /** Install state per on-device model id; empty while loading. */
  byId: ReadonlyMap<string, ModelInstallState>;
  /**
   * Why install states cannot be read here, in words (no on-device runtime in this browser, or the call
   * failed); null when they can. The screens show it instead of a download button.
   */
  unavailable: string | null;
  isLoading: boolean;
}

export function useInstalls(): Installs {
  // Progress arrives as `model` events (app/model-events.ts); the runtime re-announces its state after the
  // last progress of a burst, so the number a stalled download stopped at is not lost to the throttle.
  const q = useEngineQuery(modelKeys.installs(), (e) => e.models.installs(), { retry: false });
  const byId = new Map((q.data ?? []).map((s) => [s.modelId as string, s]));
  let unavailable: string | null = null;
  if (q.error) {
    const e = toDigesterError(q.error);
    unavailable = e.code === 'unsupported' ? 'This browser cannot run on-device models.' : `The download state cannot be read: ${e.message}`;
  }
  return { byId, unavailable, isLoading: q.isLoading };
}

export function isOnDevice(entry: ModelCatalogEntry | undefined): entry is EmbeddingCatalogEntry {
  return entry?.kind === 'onnx-embedding';
}

/** The catalogue entry of a model id. */
export function entryOf(catalog: ModelCatalog | undefined, id: string | null | undefined): ModelCatalogEntry | undefined {
  return id ? catalog?.entries.find((e) => e.id === id) : undefined;
}

/** The model's display name ('Jev 1.13'), its record's name first, the catalogue's next, else the id. */
export function modelName(id: string | null | undefined, models: readonly Model[], catalog?: ModelCatalog): string {
  if (!id) return '';
  return models.find((m) => m.id === id)?.name ?? entryOf(catalog, id)?.name ?? id;
}

/**
 * A short name for tight places (score lines in Details, chips' tooltips): "Jev" for Jev, "On-device"
 * for an on-device model when it is the only one, else its name.
 */
export function shortModelName(id: string | null | undefined, models: readonly Model[], catalog?: ModelCatalog): string {
  if (!id) return '';
  const entry = entryOf(catalog, id);
  const model = models.find((m) => m.id === id);
  const kind = entry?.kind ?? model?.kind;
  if (kind === 'remote-jev') return 'Jev';
  if (kind === 'onnx-embedding') {
    const onDevice = (catalog?.entries ?? []).filter((e) => e.kind === 'onnx-embedding').length;
    return onDevice <= 1 ? 'On-device' : modelName(id, models, catalog);
  }
  return modelName(id, models, catalog);
}
